import { ArrowRight, ExternalLink, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { buildLegacyUrl } from '@/lib/legacy-url';
import { cn } from '@/lib/utils';

import { resolveTone, TONE_CLASSES } from '../lib/module-icons';
import type { ModuloDisponible } from '../modulos.types';
import { ModuleIcon } from './module-icon';

interface ModuleCardProps {
  modulo: ModuloDisponible;
  /** Nombre de la categoría (se muestra cuando la tarjeta sale de una búsqueda). */
  categoria?: string;
}

/**
 * Tarjeta de módulo.
 *
 * Distingue visualmente los módulos ya migrados a EDSuite Web de los que siguen
 * viviendo en el panel anterior (CodeIgniter). El enlace al panel anterior sólo
 * se ofrece a los módulos pendientes: una vez migrado, el módulo se usa aquí.
 *
 * La acción ocupa todo el ancho de la tarjeta y va anclada al pie (`mt-auto`),
 * así las tarjetas de una misma fila quedan alineadas aunque la descripción
 * ocupe más o menos líneas.
 */
export function ModuleCard({ modulo, categoria }: ModuleCardProps) {
  const tone = TONE_CLASSES[resolveTone(modulo.color)];
  const legacyUrl = modulo.migrado ? null : buildLegacyUrl(modulo.ruta_legacy);

  return (
    <article
      className={cn(
        'flex flex-col rounded-card border border-ink-100 bg-white p-4 shadow-soft',
        'transition-shadow duration-200 focus-within:shadow-card hover:shadow-card',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-xl ring-1',
            tone.icon,
            tone.ring,
          )}
        >
          <ModuleIcon name={modulo.icono_web} className="size-5" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold tracking-tight text-ink-900">{modulo.nombre}</h3>

            {modulo.migrado ? (
              <Badge variant="brand">Nuevo panel</Badge>
            ) : (
              <Badge variant="outline" title="Disponible en el panel anterior">
                <Lock className="size-3" aria-hidden="true" />
                Migrando
              </Badge>
            )}
          </div>

          {categoria ? (
            <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-ink-300">
              {categoria}
            </p>
          ) : null}

          <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">
            {modulo.descripcion ?? 'Sin descripción.'}
          </p>
        </div>
      </div>

      <div className="mt-auto pt-4">
        {modulo.migrado && modulo.web_path ? (
          <Link to={modulo.web_path} className="group block">
            <Button
              className="w-full"
              rightIcon={
                <ArrowRight
                  aria-hidden="true"
                  className="size-4 transition-transform duration-200 group-hover:translate-x-0.5"
                />
              }
            >
              Abrir
            </Button>
          </Link>
        ) : legacyUrl ? (
          <a href={legacyUrl} target="_blank" rel="noreferrer noopener" className="block">
            <Button
              variant="outline"
              className="w-full"
              rightIcon={<ExternalLink className="size-3.5" aria-hidden="true" />}
            >
              Panel anterior
            </Button>
          </a>
        ) : (
          <p className="text-xs text-ink-400">Aún disponible sólo en el panel anterior.</p>
        )}
      </div>
    </article>
  );
}
